// Omat virheet: cards made from your own mistakes (Axel's decision of 2026-10-09, learning idea 1;
// docs/PEDAGOGY.md §8, docs/COURSE.md "Omat virheet"). Each person's real errors come back as contrast
// cards in the daily session: first you retrieve the right form (typed, or said and self-graded for a
// case question), then your old answer appears struck through beside it with one line from the engine
// (what the right form is, and what yours was when the engine knows it). Shared by the app (the cards,
// the collectors), the Worker (validation, api/src/routes/own-errors.ts) and the tests. Pure: no DOM,
// no Node.
//
// Where mistakes come from (OwnErrorSource): a typed card answer that was near or wrong, a fill or case
// answer in an exercise, a word written wrong in a dictation, the partner's correction of one's letter.
// The target is always a form the engine knows: a dictionary headword, a paradigm form, or a form
// verbatim in one of Peterson's lines. The partner's text and a voice letter's words count only when the
// analyser reads them as an exact known form (never a suffix-rule guess or a macron-folded match); else
// nothing is collected. Every error is private to its person.
//
// The card is an ordinary FSRS card of the person (type 'sentence', item id "virhe:<id>"), introduced
// at most OWN_ERROR_RULES.newPerDay a day, from the day after the mistake. It retires once it has been
// answered right (Osasin or Helppo) on OWN_ERROR_RULES.retireDays local days in a row: the card's state
// keeps those days (`right_days`), a miss empties them.
import { foldForSearch, normalizeHv } from '../normalize';
import type { Case, FormTags, OwnErrorSource } from '../types';
import { CASES, OWN_ERROR_SOURCES } from '../types';

export type OwnErrorAnswer = 'form' | 'case';
/**
 * What the card's front asks with: 'gloss' the meaning (a word card's typed direction: meaning →
 * High Valyrian), 'form' the headword and which form (a drill, a gap in a line).
 */
export type OwnErrorCue = 'gloss' | 'form';

/** The engine's reading of an answer as an exact known form. */
export interface OwnErrorReading {
  lemmaId: string;
  tags: FormTags;
}

export interface OwnError {
  /** Made by the client: "oe-" + 8–32 lowercase letters or digits. */
  id: string;
  source: OwnErrorSource;
  answer: OwnErrorAnswer;
  cue: OwnErrorCue;
  /** The right answer's word and form. */
  lemmaId: string;
  target: string;
  tags: FormTags;
  /** What you wrote (High Valyrian), or the case you picked (answer 'case': a Case id). */
  wrong: string;
  /** What the analyser read `wrong` as, when it is an exact known form. */
  wrongReading?: OwnErrorReading;
  /** The line it was in, the word left out: shown with a gap on the card. */
  before?: string;
  after?: string;
  /** A dictation's recordings (file names or recording refs), to hear the line again. */
  audio?: string[];
  /** The engine's reason for a case answer (exercises.ts CaseExercise.why). */
  why?: { fi: string; en: string };
  /** Where it came from (a card key, an exercise item, a correction id): one error per ref and target. */
  ref: string;
  /** How many times the same mistake was made (the same target, while the card is active). */
  times: number;
  createdAt: string;
  lastAt: string;
  /** Removed by its person ("Poista kortti"): a tombstone, so every device drops the card. */
  deletedAt?: string;
}

/** The rules as data (sources in the comments). */
export const OWN_ERROR_RULES = {
  /** "At most a few new error cards a day" (Axel 2026-10-09): they never crowd out the day's new words. */
  newPerDay: 3,
  /**
   * Right on this many local days in a row and the card retires. Successive relearning: three correct
   * recalls in separate sessions is the criterion Rawson, Dunlosky & Sciartelli (2013) found durable,
   * the same as the word cards' RELEARN.days (app/src/lib/studyRules.ts).
   */
  retireDays: 3,
  /** A grade counts as right from Osasin (3) up; Vaikea or Uudelleen starts the run again. */
  rightFrom: 3,
  /** Field limits (the Worker refuses more). */
  maxHv: 80,
  maxContext: 300,
  maxRef: 200,
  maxAudio: 4,
} as const;

export const OWN_ERROR_ID = /^oe-[a-z0-9]{8,32}$/;

/**
 * A fixed id for a ref (a letter correction): every device that collects it writes the same row
 * ("oe-" + 16 hex digits, two FNV-1a hashes).
 */
export function errorIdFor(ref: string): string {
  let a = 0x811c9dc5;
  let b = 0x01000193 ^ 0x5bd1e995;
  for (let i = 0; i < ref.length; i++) {
    const c = ref.charCodeAt(i);
    a = Math.imul(a ^ c, 0x01000193) >>> 0;
    b = Math.imul(b ^ c, 0x01000193 + 2) >>> 0;
  }
  return `oe-${a.toString(16).padStart(8, '0')}${b.toString(16).padStart(8, '0')}`;
}

// ---------- the card ----------

/** The item id of an error's card (card type 'sentence'); never contains "#" (lib/forms splitItemId). */
export const OWN_ERROR_PREFIX = 'virhe:';
export const ownErrorCardId = (id: string): string => `${OWN_ERROR_PREFIX}${id}`;
export const isOwnErrorCardId = (itemId: string): boolean => itemId.startsWith(OWN_ERROR_PREFIX);
export const ownErrorIdOf = (itemId: string): string | null => (isOwnErrorCardId(itemId) ? itemId.slice(OWN_ERROR_PREFIX.length) : null);

/** The local days with a right answer in a row after grading `grade` on `day` (YYYY-MM-DD). */
export function rightDaysAfter(prior: readonly string[] | undefined | null, grade: number, day: string): string[] {
  if (grade < OWN_ERROR_RULES.rightFrom) return [];
  const days = Array.isArray(prior) ? prior.filter((d) => typeof d === 'string') : [];
  return days.includes(day) ? [...days] : [...days, day].slice(-OWN_ERROR_RULES.retireDays);
}

/** Has the card been right on enough days in a row to retire? */
export function isRetired(state: { right_days?: unknown } | null | undefined): boolean {
  const days = state?.right_days;
  return Array.isArray(days) && days.length >= OWN_ERROR_RULES.retireDays;
}

// ---------- the contrast line ----------

/** The fields a contrast compares, in the order they are named. */
const CONTRAST_FIELDS = [
  'case',
  'number',
  'person',
  'tense',
  'mood',
  'voice',
  'gender',
  'adjPosition',
  'degree',
  'nonFinite',
  'polarity',
] as const satisfies readonly (keyof FormTags)[];

export type OwnErrorContrast =
  /** A case question: the case asked and the one picked. */
  | { kind: 'case'; asked: Case; wrote: Case }
  /** Another form of the same word: the fields where they differ, the right form's and yours. */
  | { kind: 'form'; asked: FormTags; wrote: FormTags }
  /** The same letters; the long vowels (macrons) differ. */
  | { kind: 'macrons' }
  /** A known form of another word. */
  | { kind: 'word'; lemmaId: string }
  /** Nothing known about the old answer: the right form's own tags. */
  | { kind: 'label'; asked: FormTags };

const pick = (t: FormTags, fields: readonly (keyof FormTags)[]): FormTags => {
  const out: Record<string, unknown> = {};
  for (const f of fields) if (t[f] !== undefined) out[f] = t[f];
  return out as FormTags;
};

/** What the card's rule line says (the app writes it out with engine/src/labels.ts). */
export function contrastOf(e: Pick<OwnError, 'answer' | 'lemmaId' | 'tags' | 'target' | 'wrong' | 'wrongReading'>): OwnErrorContrast {
  if (e.answer === 'case' && e.tags.case && (CASES as readonly string[]).includes(e.wrong)) return { kind: 'case', asked: e.tags.case, wrote: e.wrong as Case };
  const r = e.wrongReading;
  if (r && r.lemmaId === e.lemmaId) {
    const diff = CONTRAST_FIELDS.filter((f) => r.tags[f] !== e.tags[f]);
    if (diff.length) return { kind: 'form', asked: pick(e.tags, diff), wrote: pick(r.tags, diff) };
  }
  const a = normalizeHv(e.target, { doubledVowels: true });
  const b = normalizeHv(e.wrong, { doubledVowels: true });
  if (a !== b && foldForSearch(a) === foldForSearch(b)) return { kind: 'macrons' };
  if (r && r.lemmaId !== e.lemmaId) return { kind: 'word', lemmaId: r.lemmaId };
  return { kind: 'label', asked: pick(e.tags, CONTRAST_FIELDS) };
}

// ---------- collecting ----------

const tagsKey = (t: FormTags): string =>
  Object.keys(t)
    .sort()
    .map((k) => `${k}=${String(t[k as keyof FormTags])}`)
    .join(',');

/** One active card per right answer: the same word, form and kind of question. */
export const errorKey = (e: Pick<OwnError, 'answer' | 'lemmaId' | 'target' | 'tags'>): string =>
  `${e.answer}|${e.lemmaId}|${normalizeHv(e.target)}|${tagsKey(e.tags)}`;

/** A mistake as a collector sees it (id, count and times are the store's). */
export type OwnErrorInput = Omit<OwnError, 'id' | 'times' | 'createdAt' | 'lastAt' | 'deletedAt'>;

/**
 * The error record to write for a mistake, or null when there is nothing to write. A letter
 * correction is collected once (its ref), whatever became of its card. Any other mistake joins the
 * active card of the same right answer (times + 1, your latest answer shown); a retired or removed
 * card does not take it, so the same mistake made again later starts a new card.
 */
export function recordError(
  list: readonly OwnError[],
  input: OwnErrorInput,
  opts: { newId: string; now: string; retired: (id: string) => boolean },
): OwnError | null {
  const target = normalizeHv(input.target);
  const wrong = input.answer === 'case' ? input.wrong : normalizeHv(input.wrong);
  if (!target || !wrong || !input.lemmaId) return null;
  if (input.answer === 'form' && normalizeHv(wrong, { doubledVowels: true }) === normalizeHv(target, { doubledVowels: true })) return null;
  if (input.source === 'letter' && list.some((e) => e.ref === input.ref)) return null;
  const key = errorKey({ ...input, target });
  const active = list.find((e) => !e.deletedAt && !opts.retired(e.id) && errorKey(e) === key);
  if (active) {
    // the same answer again on the same item the same minute is a replay, not a new mistake
    if (active.ref === input.ref && active.wrong === wrong && active.lastAt.slice(0, 16) === opts.now.slice(0, 16)) return null;
    // the latest mistake is the one shown (its own context and reading, never the older one's)
    return { ...input, target, wrong, id: active.id, times: active.times + 1, createdAt: active.createdAt, lastAt: opts.now };
  }
  return { ...input, target, wrong, id: opts.newId, times: 1, createdAt: opts.now, lastAt: opts.now };
}

// ---------- which come today ----------

/**
 * The errors whose cards may start today, best first: not removed, no card yet, made before
 * `dayStart` (a mistake comes back from the next day, not in the session it was made in); those made
 * more often first, then the oldest. At most `room`.
 */
export function newErrorPicks(errors: readonly OwnError[], started: (id: string) => boolean, dayStart: number, room: number): OwnError[] {
  if (room <= 0) return [];
  return errors
    .filter((e) => !e.deletedAt && !started(e.id) && Date.parse(e.createdAt) < dayStart)
    .sort((a, b) => b.times - a.times || a.createdAt.localeCompare(b.createdAt) || a.id.localeCompare(b.id))
    .slice(0, room);
}

// ---------- the course's gates (the lesson metas' `ownErrors`, docs/COURSE.md) ----------

/** The lesson (order) that starts collecting each source; null: no lesson does. */
export type OwnErrorGates = Record<OwnErrorSource, number | null>;

/** The first lesson naming a source in its meta starts it, as the form drills open by `drills`. */
export function ownErrorGates(metas: readonly { order: number; ownErrors?: readonly string[] }[]): OwnErrorGates {
  const out = Object.fromEntries(OWN_ERROR_SOURCES.map((s) => [s, null])) as OwnErrorGates;
  for (const m of [...metas].sort((a, b) => a.order - b.order)) {
    for (const s of m.ownErrors ?? []) if (s in out && out[s as OwnErrorSource] === null) out[s as OwnErrorSource] = m.order;
  }
  return out;
}

/** Is the source collected with `lessonsOpen` lessons open? */
export const ownErrorOpen = (gates: OwnErrorGates, source: OwnErrorSource, lessonsOpen: number): boolean =>
  gates[source] !== null && lessonsOpen >= gates[source]!;

// ---------- reading a record (the Worker's validation, the app's cached copy) ----------

const str = (x: unknown, max: number): string | null => (typeof x === 'string' && x.length <= max ? x.normalize('NFC') : null);
const TAG_VALUE = /^[a-z0-9-]{1,24}$/;

function tagsOf(x: unknown): FormTags | null {
  if (!x || typeof x !== 'object' || Array.isArray(x)) return null;
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(x as Record<string, unknown>)) {
    if (!/^[a-zA-Z]{1,16}$/.test(k)) return null;
    if (typeof v === 'number' && Number.isInteger(v) && v >= 1 && v <= 3) out[k] = v;
    else if (typeof v === 'boolean') out[k] = v;
    else if (typeof v === 'string' && TAG_VALUE.test(v)) out[k] = v;
    else return null;
  }
  return out as FormTags;
}

const ISO = (x: unknown): string | null => (typeof x === 'string' && x.length <= 40 && !Number.isNaN(Date.parse(x)) ? x : null);

/** A well-formed error record, or null (the reason for the Worker's 400). Unknown keys are dropped. */
export function readOwnError(raw: unknown): { ok: OwnError } | { error: string } {
  if (!raw || typeof raw !== 'object') return { error: 'not an object' };
  const r = raw as Record<string, unknown>;
  const R = OWN_ERROR_RULES;
  const id = typeof r.id === 'string' && OWN_ERROR_ID.test(r.id) ? r.id : null;
  if (!id) return { error: 'id must be "oe-" + 8–32 lowercase letters or digits' };
  const source = OWN_ERROR_SOURCES.find((s) => s === r.source);
  if (!source) return { error: `source must be one of ${OWN_ERROR_SOURCES.join(', ')}` };
  const answer = r.answer === 'form' || r.answer === 'case' ? r.answer : null;
  if (!answer) return { error: 'answer must be form or case' };
  const cue = r.cue === 'gloss' || r.cue === 'form' ? r.cue : null;
  if (!cue) return { error: 'cue must be gloss or form' };
  const lemmaId = str(r.lemmaId, 200);
  const target = str(r.target, R.maxHv);
  const wrong = str(r.wrong, R.maxHv);
  const ref = str(r.ref, R.maxRef);
  if (!lemmaId || !target?.trim() || !wrong?.trim() || !ref)
    return { error: 'lemmaId, target, wrong and ref are required (target and wrong at most 80 characters)' };
  if (answer === 'case' && !(CASES as readonly string[]).includes(wrong)) return { error: 'a case answer is a case id' };
  const tags = tagsOf(r.tags ?? {});
  if (!tags) return { error: 'tags must be form tags' };
  const out: OwnError = { id, source, answer, cue, lemmaId, target, tags, wrong, ref, times: 1, createdAt: '', lastAt: '' };
  if (r.wrongReading !== undefined && r.wrongReading !== null) {
    const w = r.wrongReading as Record<string, unknown>;
    const wl = typeof w === 'object' ? str(w.lemmaId, 200) : null;
    const wt = typeof w === 'object' ? tagsOf(w.tags ?? {}) : null;
    if (!wl || !wt) return { error: 'wrongReading must be { lemmaId, tags }' };
    out.wrongReading = { lemmaId: wl, tags: wt };
  }
  for (const k of ['before', 'after'] as const) {
    if (r[k] === undefined || r[k] === null || r[k] === '') continue;
    const v = str(r[k], R.maxContext);
    if (v === null) return { error: `${k} must be text of at most ${R.maxContext} characters` };
    out[k] = v;
  }
  if (r.audio !== undefined && r.audio !== null) {
    const a = Array.isArray(r.audio) ? r.audio : null;
    if (!a || a.length > R.maxAudio || a.some((x) => str(x, 200) === null)) return { error: `audio must be at most ${R.maxAudio} file names` };
    if (a.length) out.audio = a as string[];
  }
  if (r.why !== undefined && r.why !== null) {
    const w = r.why as Record<string, unknown>;
    const fi = typeof w === 'object' ? str(w.fi, R.maxContext) : null;
    const en = typeof w === 'object' ? str(w.en, R.maxContext) : null;
    if (fi === null || en === null) return { error: 'why must be { fi, en }' };
    out.why = { fi, en };
  }
  const times = Number(r.times ?? 1);
  out.times = Number.isInteger(times) && times >= 1 ? Math.min(times, 9999) : 1;
  const createdAt = ISO(r.createdAt);
  const lastAt = ISO(r.lastAt ?? r.createdAt);
  if (!createdAt || !lastAt) return { error: 'createdAt must be an ISO time' };
  out.createdAt = createdAt;
  out.lastAt = lastAt;
  const deletedAt = r.deletedAt === undefined || r.deletedAt === null ? null : ISO(r.deletedAt);
  if (deletedAt) out.deletedAt = deletedAt;
  return { ok: out };
}
